<script setup lang="ts">
/**
 * Full-screen mobile overlay for a single client's detail view.
 *
 * Rendered as a fixed-position layer (z-index 10, above MobileBottomNav's 5)
 * via Teleport so it covers the tab bar. The sticky iOS-style navbar provides
 * back navigation and an Edit button. ClientCard beneath the navbar manages
 * its own internal scroll for the visits ledger.
 *
 * Edit opens AddClientFormMobileScreen via the Pinia store. App.vue renders
 * that form (z-index 11) whenever addClientFormStore.isOpen && isMobile, so
 * it covers this card until the user saves or cancels.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuery } from '@tanstack/vue-query'
import ClientCard from './ClientCard.vue'
import { fetchClient } from '@/api/clients'
import { clientCardPollingInterval } from '@/config/api'
import { useAddClientFormStore } from '@/stores/add_client_form_store'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'

const props = defineProps<{
  clientId: string
}>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { hasRole } = usePermissions()
const canEdit = computed(() => hasRole(ResourceAccessRole.ClientsEdit))

const clientQuery = useQuery({
  queryKey: computed(() => ['client', props.clientId]),
  queryFn: () => fetchClient(props.clientId),
  staleTime: clientCardPollingInterval,
  refetchInterval: clientCardPollingInterval,
  refetchIntervalInBackground: false,
})

const person = computed(() => clientQuery.data.value ?? null)

const addClientFormStore = useAddClientFormStore()

function openEdit(): void {
  const p = person.value
  if (!p) return
  const rawGender = p.gender?.trim().toUpperCase()
  const gender: 'MALE' | 'FEMALE' = rawGender === 'MALE' || rawGender === 'M' ? 'MALE' : 'FEMALE'
  // Intentionally no clearInfoViewSelections here — on mobile that helper also
  // clears the clientCard selection, which would dismiss this screen.
  addClientFormStore.openForEdit(p.id, {
    name: p.name ?? '',
    gender,
    phone: p.phone ?? null,
    email: p.email ?? null,
    roleIds: (p.roles ?? []).map((r) => r.id),
    startDatetime: p.start_datetime ?? null,
    birthday: null,
  })
}
</script>

<template>
  <Teleport to="body">
    <div class="mobile-client-screen font-tufte text-tufte-text">
      <nav class="mobile-client-navbar">
        <button type="button" class="navbar-back" @click="emit('close')">
          <svg
            width="8"
            height="14"
            viewBox="0 0 8 14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 1L1 7L7 13" />
          </svg>
          {{ t('tabs.clients.title') }}
        </button>
        <span class="navbar-title">{{ t('common.client') }}</span>
        <button
          v-if="canEdit"
          type="button"
          class="navbar-edit"
          :disabled="!person"
          @click="openEdit"
        >
          {{ t('common.edit') }}
        </button>
      </nav>

      <div class="mobile-client-body">
        <ClientCard :key="clientId" :client-id="clientId" :hide-edit-btn="true" />
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.mobile-client-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: var(--color-tufte-bg);
  display: flex;
  flex-direction: column;
}

.mobile-client-navbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem 0.5rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.navbar-back {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  justify-content: flex-start;
}

.navbar-back:active {
  opacity: 0.6;
}

.navbar-title {
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  flex: 1;
  text-align: center;
}

.navbar-edit {
  font: inherit;
  font-size: 0.9rem;
  text-transform: uppercase;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  text-align: right;
}

.navbar-edit:disabled {
  color: var(--color-tufte-muted);
  cursor: default;
}

.navbar-edit:not(:disabled):active {
  opacity: 0.6;
}

.mobile-client-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* override the desktop panel's max-width constraint for full-width mobile */
.mobile-client-body :deep(.client-card) {
  max-width: none;
  margin: 0;
}
</style>
